import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  UserManagementDropdownOptions,
  UserManagementListResponse,
  UserManagementModel,
  UserManagementOption,
  UserManagementPayload,
  AcademicRankModel,
  PositionModel,
  RoleModel,
} from '../../models/user-management/user-management.model';

export interface UserManagementListParams {
  keywords?: string | null;
  position_id?: number | string | null;
  department_id?: number | string | null;
  role_id?: number | string | null;
  page?: number;
  limit?: number;
  sort?: string | null;
  direction?: string | null;
  datatable?:boolean | null;
}

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  private apiUrl = '/users';
  private positionsUrl = '/positions';
  private departmentsUrl = '/departments';
  private academicRanksUrl = '/academic_ranks';
  private rolesUrl = '/roles';

  #http = inject(HttpClient);

  getUsersList(params?: UserManagementListParams): Observable<UserManagementListResponse> {
    return this.#http.get<unknown>(this.apiUrl, {
      params: this.buildListParams(params),
    }).pipe(
      map((res) => this.normalizeUsersListResponse(res, params))
    );
  }
  getDropdownOptions(positionId?: number | null): Observable<UserManagementDropdownOptions> {
    let params = new HttpParams();

    if (positionId != null) {
      params = params.set('position_id', String(positionId));
    }

    return this.#http.get<UserManagementDropdownOptions>(`${this.apiUrl}/dropdown_options`, { params });
  }

  getPositions(): Observable<PositionModel[]> {
    return this.#http.get<PositionModel[]>(this.positionsUrl);
  }

  getDepartments(datatable?:boolean|null): Observable<UserManagementOption[]> {
    let params = new HttpParams();
    params = params.set('datatable',datatable??true);
    return this.#http.get<UserManagementOption[]>(this.departmentsUrl,{params});
  }

  getAcademicRanks(): Observable<AcademicRankModel[]> {
    return this.#http.get<AcademicRankModel[]>(this.academicRanksUrl);
  }

  getRoles(): Observable<RoleModel[]> {
    return this.#http.get<RoleModel[]>(this.rolesUrl);
  }

  getUserById(id: number): Observable<UserManagementModel> {
    return this.#http.get<UserManagementModel>(`${this.apiUrl}/${id}`);
  }

  createUser(payload: UserManagementPayload): Observable<UserManagementModel> {
    return this.#http.post<UserManagementModel>(this.apiUrl, payload);
  }

  updateUser(id: number, payload: UserManagementPayload): Observable<UserManagementModel> {
    return this.#http.put<UserManagementModel>(`${this.apiUrl}/${id}`, payload);
  }

  exportUsers(): Observable<Blob> {
    return this.#http.get(`${this.apiUrl}/export`, {
      responseType: 'blob',
    });
  }

  importUsers(file: File): Observable<unknown> {
    const formData = new FormData();

    formData.append('file', file);

    return this.#http.post(`${this.apiUrl}/import`, formData);
  }

  deleteUser(id: number): Observable<void> {
    return this.#http.delete<void>(`${this.apiUrl}/${id}`);
  }

   public buildListParams(params?: UserManagementListParams): HttpParams {
    let httpParams = new HttpParams().set('datatable', 'true');
      if (params?.datatable != null) {
      httpParams = httpParams.set('datatable', params.datatable);
    }

    if (params?.keywords != null && params.keywords !== '') {
      httpParams = httpParams.set('keywords', params.keywords);
    }

    if (params?.position_id != null && params.position_id !== '') {
      httpParams = httpParams.set('position_id', String(params.position_id));
    }

    if (params?.department_id != null && params.department_id !== '') {
      httpParams = httpParams.set('department_id', String(params.department_id));
    }

    if (params?.role_id != null && params.role_id !== '') {
      httpParams = httpParams.set('role_id', String(params.role_id));
    }

    httpParams = this.setParamIfPresent(httpParams, 'page', params?.page);
    httpParams = this.setParamIfPresent(httpParams, 'limit', params?.limit);
    httpParams = this.setParamIfPresent(httpParams, 'sort', params?.sort);
    httpParams = this.setParamIfPresent(httpParams, 'direction', params?.direction);

    return httpParams;
  }

  private setParamIfPresent(
    httpParams: HttpParams,
    key: string,
    value: string | number | null | undefined
  ): HttpParams {
    if (value == null || value === '') {
      return httpParams;
    }

    return httpParams.set(key, String(value));
  }

  private normalizeUsersListResponse(
    res: unknown,
    params?: UserManagementListParams
  ): UserManagementListResponse {
    if (res !== null && typeof res === 'object' && !Array.isArray(res)) {
      const response = res as Record<string, unknown>;

      if (Array.isArray(response.data)) {
        return {
          data: response.data as UserManagementModel[],
          page: Number(response.page ?? params?.page ?? 1),
          limit: Number(response.limit ?? params?.limit ?? 10),
          total: Number(response.total ?? response.data.length),
          total_pages: Number(response.total_pages ?? 1),
        };
      }
    }

    const data = Array.isArray(res) ? res as UserManagementModel[] : [];
    const limit = params?.limit ?? 10;

    return {
      data,
      page: params?.page ?? 1,
      limit,
      total: data.length,
      total_pages: Math.max(1, Math.ceil(data.length / limit)),
    };
  }
}
